import { useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../styling/stylingHelper';
import type { OrderLookupProduct, OrderLookupFactuur } from '../../../../../services/admin/adminService';
import { GmailIdCopy } from '../../../orderHistory/GmailCopy';
import { ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { calculateProductTotal } from '../../../../../utils/platformUtils';
import { calculateUnitPrice } from '../../../../orderhistory/aggregations';
import { formatQuantityInfo as formatProductQuantityInfo } from '../../../../../utils/quantityUtils';
import { renderByType } from '../../../../ui/TableValueComponent';
import type { TFn } from '../types';

// Read-only product row for the Order Lookup expandable dropdown.
// Mirrors the OrderHistoryDetailPage product display so super-admins can see
// exactly what products are inside an order.
export function OrderLookupProductItem({ product, platform }: { product: OrderLookupProduct; platform: string }) {
  const p = product as any;
  const productTotal = calculateProductTotal(p, platform);
  const quantityForDisplay = product.rawQuantity || product.quantity;
  const quantityDisplay = product.quantityInfo
    ? formatProductQuantityInfo(quantityForDisplay, product.quantityInfo as any, product.category ?? undefined)
    : null;
  const unitPrice = calculateUnitPrice(product.price, product.quantityInfo as any, product.category ?? undefined);

  return (
    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex-1">
        {(quantityDisplay || product.category) && (
          <div className="mb-2 flex items-center gap-2">
            {quantityDisplay && (
              <span className="inline-block px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-md">
                {quantityDisplay.displayText}
              </span>
            )}
            {unitPrice && (
              <span className="inline-block px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-xs font-medium rounded-md">
                €{unitPrice.displayText}
              </span>
            )}
            {product.category && (
              <span className="inline-block">{renderByType(product.category, 'category')}</span>
            )}
          </div>
        )}
        <div className={cn(primaryText, semiBoldText, smallTextSize)}>
          {product.name || 'Unknown Product'}
        </div>
        {product.articleId && product.articleId !== 'no_article_id' && (
          <div className={cn(secondaryText, 'text-xs font-mono mt-1 flex items-center gap-3')}>
            <span>ID: {product.articleId}</span>
            {product.rawQuantity !== null && product.rawQuantity !== undefined && (
              <span>Qty: {product.rawQuantity}</span>
            )}
          </div>
        )}
      </div>
      <div className="text-right ml-4">
        <div className={cn(primaryText, semiBoldText)}>€{productTotal.toFixed(2)}</div>
        <div className={cn(secondaryText, 'text-xs font-medium')}>
          {product.quantity.toFixed(1)} × €{(product.price || 0).toFixed(2)}
        </div>
      </div>
    </div>
  );
}

/**
 * Linked-factuur block inside an Order Lookup dropdown. Wherever a factuur_orders row is linked it IS
 * the read path (applyFactuurOverride substitutes its product_data/totals into order reads), so the
 * lookup shows the invoice totals and its line items next to the pakbon's, plus the total delta.
 */
export function OrderLookupFactuurBlock({
  factuur,
  platform,
  orderTotal,
  t,
}: {
  factuur: OrderLookupFactuur;
  platform: string;
  orderTotal: number;
  t: TFn;
}) {
  const [showLines, setShowLines] = useState(false);
  const delta = factuur.orderTotal !== null ? factuur.orderTotal - orderTotal : null;
  const fmt = (v: number | null) => (v !== null && v !== undefined ? `€${v.toFixed(2)}` : '—');

  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-3">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="px-2 py-0.5 rounded bg-amber-500 dark:bg-amber-500 text-white dark:text-white text-xs font-semibold">
          {t('adminTesting.orderLookup.factuur.badge')}
        </span>
        <span className={cn(primaryText, smallTextSize, semiBoldText)}>{factuur.invoiceNumber}</span>
        <span className={cn(secondaryText, 'text-xs')}>
          {new Date(factuur.invoiceDate).toLocaleDateString('nl-NL')}
        </span>
        {factuur.orderNumber && (
          <span className={cn(secondaryText, 'text-xs font-mono')}>
            {t('adminTesting.orderLookup.factuur.orderNumber', { number: factuur.orderNumber })}
          </span>
        )}
        <div className="flex items-center gap-3 ml-auto">
          <GmailIdCopy gmailId={factuur.gmailId ?? undefined} />
          {factuur.sourceFileUrl ? (
            <a
              href={factuur.sourceFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 dark:text-textBlue-dark hover:underline whitespace-nowrap"
            >
              <FileText className="w-4 h-4" />
              {t('adminTesting.orderLookup.factuur.seeFile')}
            </a>
          ) : (
            <span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.orderLookup.factuur.noFile')}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mb-2">
        {[
          { label: t('adminTesting.orderLookup.factuur.orderTotal'), value: fmt(factuur.orderTotal) },
          { label: t('adminTesting.orderLookup.factuur.orderTotalWithTax'), value: fmt(factuur.orderTotalWithTaxes) },
          { label: t('adminTesting.orderLookup.factuur.productSum'), value: fmt(factuur.productSum) },
          { label: t('adminTesting.orderLookup.factuur.invoiceTotal'), value: fmt(factuur.invoiceTotal) },
          { label: t('adminTesting.orderLookup.factuur.invoiceTotalWithTax'), value: fmt(factuur.invoiceTotalWithTaxes) },
          { label: t('adminTesting.orderLookup.factuur.discount'), value: fmt(factuur.discount) },
          { label: t('adminTesting.orderLookup.factuur.shipping'), value: fmt(factuur.shipping) },
        ].map(({ label, value }) => (
          <div key={label}>
            <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{label}</div>
            <div className={cn(primaryText, 'text-xs', semiBoldText)}>{value}</div>
          </div>
        ))}
      </div>

      {delta !== null && Math.abs(delta) >= 0.01 && (
        <div className={cn('inline-block px-2 py-1 rounded text-xs mb-2', 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300')}>
          {t('adminTesting.orderLookup.factuur.delta', {
            delta: `${delta > 0 ? '+' : ''}${delta.toFixed(2)}`,
            orderTotal: orderTotal.toFixed(2),
          })}
        </div>
      )}

      <button
        onClick={() => setShowLines(v => !v)}
        className={cn('flex items-center gap-1.5', secondaryText, 'text-xs hover:underline')}
      >
        {showLines ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {t('adminTesting.orderLookup.factuur.lines', { count: factuur.products.length })}
      </button>

      {showLines && (
        factuur.products.length === 0 ? (
          <div className={cn(secondaryText, 'text-xs mt-2')}>{t('adminTesting.orderLookup.factuur.noLines')}</div>
        ) : (
          <div className="space-y-2 mt-2">
            {[...factuur.products]
              .sort((a, b) => calculateProductTotal(b as any, platform) - calculateProductTotal(a as any, platform))
              .map((p, i) => (
                <OrderLookupProductItem key={p.articleId || i} product={p} platform={platform} />
              ))}
          </div>
        )
      )}
    </div>
  );
}
